/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { useSelector } from "react-redux";

import * as MVPBenefitsSelector from "Dataflows/MVP/Benefits/MVPBenefitsSelector";
import * as MVPActions from "Dataflows/MVP/MVPAction";

import { useAuth } from "Authentication/AuthProvider";
import ProfileBenefitDetails from "Components/ProfileBenefitDetails/ProfileBenefitDetails";
import { OperationStatus } from "Constants/OperationStatus";
import { RolesEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";

const MostValuableProfessionalBenefitDetails = (): React.ReactElement => {
  const profileBenefits = useSelector(MVPBenefitsSelector.getProfileBenefits);
  const profileBenefitsErrorMessage = useSelector(
    MVPBenefitsSelector.getProfileBenefitsErrorMessage
  );
  const profileBenefitsStatus = useSelector(MVPBenefitsSelector.getProfileBenefitsStatus);
  const isClaimingBenefitStatus = useSelector(MVPBenefitsSelector.getIsClaimingBenefitStatus);
  const claimingBenefitError = useSelector(MVPBenefitsSelector.getIsClaimingBenefitsError);

  const m365BenefitDetail = useSelector(MVPBenefitsSelector.m365GetBenefitDetail);
  const m365FetchErrorMessage = useSelector(MVPBenefitsSelector.m365FetchErrorMessage);
  const m365UpdateErrorMessage = useSelector(MVPBenefitsSelector.m365UpdateErrorMessage);
  const m365FetchStatus = useSelector(MVPBenefitsSelector.m365FetchStatus);
  const m365UpdateStatus = useSelector(MVPBenefitsSelector.m365UpdateStatus);
  const ms365BenefitDetail = useSelector(MVPBenefitsSelector.ms365GetBenefitDetail);
  const ms365FetchErrorMessage = useSelector(MVPBenefitsSelector.ms365FetchErrorMessage);
  const ms365UpdateErrorMessage = useSelector(MVPBenefitsSelector.ms365UpdateErrorMessage);
  const ms365FetchStatus = useSelector(MVPBenefitsSelector.ms365FetchStatus);
  const ms365UpdateStatus = useSelector(MVPBenefitsSelector.ms365UpdateStatus);
  const teamsBenefitDetail = useSelector(MVPBenefitsSelector.teamsGetBenefitDetail);
  const teamsFetchStatus = useSelector(MVPBenefitsSelector.teamsFetchStatus);
  const teamsFetchErrorMessage = useSelector(MVPBenefitsSelector.teamsFetchErrorMessage);
  const awardKitsBenefitDetail = useSelector(MVPBenefitsSelector.awardKitsGetBenefitDetail);
  const awardKitsFetchStatus = useSelector(MVPBenefitsSelector.awardKitsFetchStatus);
  const awardKitsFetchErrorMessage = useSelector(MVPBenefitsSelector.awardKitsFetchErrorMessage);

  const azureBenefitExtendCreditError = useSelector(
    MVPBenefitsSelector.azureBenefitUpdateErrorMessage
  );
  const addAzureBenefitError = useSelector(MVPBenefitsSelector.getAddAzureBenefitErrorMessage);

  const azureBenefitExtendCreditStatus = useSelector(MVPBenefitsSelector.azureBenefitUpdateStatus);

  const { hasRole } = useAuth();

  const canManageBenefits =
    hasRole(RolesEnum.MVP_BENEFITS_MANAGER) || hasRole(RolesEnum.MVP_PROGRAM_OWNER);

  return (
    <ProfileBenefitDetails
      profileBenefits={profileBenefits}
      profileBenefitsErrorMessage={profileBenefitsErrorMessage}
      profileBenefitsStatus={profileBenefitsStatus as OperationStatus}
      isClaimingBenefitStatus={isClaimingBenefitStatus as OperationStatus}
      claimingBenefitError={claimingBenefitError}
      canManageBenefits={canManageBenefits}
      getProfileBenefits={MVPActions.fetchProfileBenefits}
      claimBenefitReset={MVPActions.claimBenefitReset}
      claimBenefit={MVPActions.claimBenefit}
      claimBenefitManager={MVPActions.claimBenefitManager}
      tenantName={TenantTypesEnum.MVP}
      m365GetBenefitDetail={MVPActions.fetchM365BenefitDetail}
      m365UpdateBenefitDetail={MVPActions.updateM365BenefitDetail}
      m365BenefitDetailReset={MVPActions.m365BenefitDetailReset}
      m365FetchErrorMessage={m365FetchErrorMessage}
      m365UpdateErrorMessage={m365UpdateErrorMessage}
      m365FetchStatus={m365FetchStatus as OperationStatus}
      m365UpdateStatus={m365UpdateStatus as OperationStatus}
      m365BenefitDetail={m365BenefitDetail}
      ms365GetBenefitDetail={MVPActions.fetchMS365BenefitDetail}
      ms365UpdateBenefitDetail={MVPActions.updateMS365BenefitDetail}
      ms365BenefitDetailReset={MVPActions.ms365BenefitDetailReset}
      ms365FetchErrorMessage={ms365FetchErrorMessage}
      ms365UpdateErrorMessage={ms365UpdateErrorMessage}
      ms365FetchStatus={ms365FetchStatus as OperationStatus}
      ms365UpdateStatus={ms365UpdateStatus as OperationStatus}
      ms365BenefitDetail={ms365BenefitDetail}
      teamsGetBenefitDetail={MVPActions.fetchTeamsBenefitDetail}
      teamsFetchStatus={teamsFetchStatus as OperationStatus}
      teamsBenefitDetail={teamsBenefitDetail}
      teamsFetchErrorMessage={teamsFetchErrorMessage}
      awardKitsGetBenefitDetail={MVPActions.fetchAwardKitsBenefitDetail}
      awardKitsFetchStatus={awardKitsFetchStatus as OperationStatus}
      awardKitsBenefitDetail={awardKitsBenefitDetail}
      awardKitsFetchErrorMessage={awardKitsFetchErrorMessage}
      extendAzureBenefitCredit={MVPActions.extendAzureBenefitCredit}
      extendAzureBenefitCreditReset={MVPActions.extendAzureBenefitCreditReset}
      extendAzureBenefitCreditUpdateStatus={azureBenefitExtendCreditStatus as OperationStatus}
      extendAzureBenefitCreditErrorMessage={azureBenefitExtendCreditError}
      addAzureBenefits={MVPActions.addAzureBenefitDetail}
      addAzureBenefitReset={MVPActions.addAzureBenefitReset}
      addAzureBenefitsUpdateStatus={azureBenefitExtendCreditStatus as OperationStatus}
      addAzureBenefitsErrorMessage={addAzureBenefitError}
    />
  );
};

export default MostValuableProfessionalBenefitDetails;
